import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  FaCalendarAlt,
  FaClock,
  FaPlus,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import { IoEye } from "react-icons/io5";
import "react-loading-skeleton/dist/skeleton.css";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import { MdOutlineEdit } from "react-icons/md";
import { useSelector } from "react-redux";
import ActionMenu from "../../../component/ActionMenu";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import Tooltip from "../../../component/Tooltip";
import i18n from "../../../i8next";
import {
  useDeleteDriverScheduleMutation,
  useGetDriverschedulesQuery,
  useUpdateDriverScheduleStatusMutation,
} from "../../../store/api/vendor/driversSlice";
import { getErrorMessage } from "../../../utils";
import {
  capitalizeFirstLetter,
  capitalizeString,
  convertTime,
  formatDateToLocal,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";

const initialActions = {
  status: "",
  shift_type: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

// Helper function to get status class and label
export const getScheduleStatusInfo = (status, t) => {
  if (!status) return { className: "status unknown", label: t ? t("status_unknown") : "Unknown" };

  const statusLower = status.toLowerCase();

  switch (statusLower) {
    case "scheduled":
      return { className: "status scheduled", label: t ? t("status_scheduled") : "Scheduled" };
    case "available":
      return { className: "status available", label: t ? t("duty_available") : "Available" };
    case "completed":
      return { className: "status completed", label: t ? t("status_completed") : "Completed" };
    case "cancelled":
      return { className: "status cancelled", label: t ? t("status_cancelled") : "Cancelled" };
    default:
      return { className: "status unknown", label: status.charAt(0).toUpperCase() + status.slice(1) };
  }
};

// Helper function to capitalize days
const capitalizeDays = (days) => {
  if (!days || !Array.isArray(days)) return "N/A";
  return days
    .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
    .join(", ");
};

const STATUS_OPTIONS = ["scheduled", "in_progress", "completed", "cancelled"];

const Schedules = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [schedules, setSchedules] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);
  const [scheduleDetails, setScheduleDetails] = useState(null);
  const [showScheduleDetails, setShowScheduleDetails] = useState(false);
  const [openStatusId, setOpenStatusId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;
  const isAdmin = role === "admin";

  const { data, isSuccess, isLoading, isError, error, isFetching, refetch } =
    useGetDriverschedulesQuery(
      { page: pagination.page, limit: pagination.limit, ...action, id },
      { skip: !id },
    );

  // UPDATE DRIVER SCHEDULE STATUS API
  const [updateDriverScheduleStatus, { isLoading: isUpdateLoading }] =
    useUpdateDriverScheduleStatusMutation();

  const [deleteDriverSchedule, { isLoading: isDeleteLoading }] =
    useDeleteDriverScheduleMutation();

  const handleDeleteConfirm = async () => {
    toast.dismiss();
    try {
      await deleteDriverSchedule(deleteTarget.id).unwrap();
      toast.success(t("schedule_deleted_success"));
      setDeleteTarget(null);
    } catch (err) {
      handleApiError(err, t);
    }
  };

  if (isError) {
    toast.dismiss();
    toast.error(getErrorMessage(error));
  }

  useEffect(() => {
    if (isSuccess && data?.data) {
      setSchedules(data?.data?.schedules);
      setPagination(data?.data?.pagination);
    }
  }, [data, isSuccess]);

  // filters chnages
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAction((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ limit: 10, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };
  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
  };

  const handleShowScheduleDetails = (schedule) => {
    setScheduleDetails(schedule);
    setShowScheduleDetails(true);
  };

  const handleCloseModal = () => {
    setShowScheduleDetails(false);
  };

  const handleStatusChange = async (schedule, newStatus) => {
    setOpenStatusId(null);
    if (schedule.status === newStatus) {
      return;
    }

    try {
      const res = await updateDriverScheduleStatus({
        id: schedule.id,
        data: { status: newStatus },
      }).unwrap();

      toast.success(res.message);
    } catch (err) {
      handleApiError(err, t);
    }
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")} {t("Schedules")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              name="shift_type"
              value={action.shift_type}
              onChange={handleChange}
            >
              <option value="">
                {t("shift")} {t("type")}
              </option>
              <option value="morning">{t("morning")}</option>
              <option value="afternoon">{t("afternoon")}</option>
              <option value="morning_afternoon">
                {t("morning")} & {t("afternoon")}
              </option>
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              name="status"
              value={action.status}
              onChange={handleChange}
            >
              <option value="">
                {t("duty")} {t("status")}
              </option>
              <option value="scheduled">{t("scheduled")}</option>
              <option value="in_progress">{t("in_progress")}</option>
              <option value="completed">{t("completed")}</option>
              <option value="cancelled">{t("cancelled")}</option>
            </select>
          </div>
          <div className="d-flex gap-2 flex-grow-1">
            <button
              className="btn btn-primary addDriver flex-grow-1"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {!isAdmin && (
              <NavLink
                to="add"
                className="page-add-btn d-flex align-items-center justify-content-center flex-grow-1 gap-2"
              >
                <FaPlus /> {t("add")} {t("Schedules")}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-nowrap">{`${t("Sr")}. ${t("no")}.`}</th>
              <th>
                {t("schedule")} {t("date")}
              </th>
              <th>
                {t("shift")} {t("type")}
              </th>
              <th>
                {t("shift")} {t("time")}
              </th>
              <th
                className="text-nowrap"
                style={{ minWidth: "250px", width: "auto" }}
              >
                {t("recurring")}
              </th>
              {!isAdmin && <th>{t("status")}</th>}
              {/* <th>{t("created_by")}</th> */}
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading length={isAdmin ? 6 : 7} />
            ) : schedules?.length > 0 ? (
              schedules?.map((schedule, i) => {
                const statusInfo = getScheduleStatusInfo(schedule?.status, t);

                return (
                  <tr key={i}>
                    <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td className="text-nowrap">
                      {formatDateToLocal(schedule?.start_date) || "N/A"} -{" "}
                      {formatDateToLocal(schedule?.end_date) || "N/A"}
                    </td>
                    <td>
                      <span className="badge bg-info">
                        {t(schedule?.shift_type) || "N/A"}
                      </span>
                    </td>
                    <td className="text-nowrap">
                      <div>
                        <small className="text-muted me-1">
                          {schedule?.shift_type === "morning_afternoon"
                            ? t("morning")
                            : t(schedule?.shift_type)}
                          :
                        </small>
                        {convertTime(schedule?.start_time) || "N/A"} –{" "}
                        {convertTime(schedule?.end_time) || "N/A"}
                      </div>
                      {schedule?.shift_type === "morning_afternoon" &&
                        schedule?.return_start_time && (
                          <div className="mt-1">
                            <small className="text-muted me-1">
                              {t("afternoon")}:
                            </small>
                            {convertTime(schedule.return_start_time)} –{" "}
                            {convertTime(schedule.return_end_time)}
                          </div>
                        )}
                    </td>
                    <td style={{ minWidth: "250px" }}>
                      <div className="d-flex flex-column">
                        <span className="text-success mb-1">
                          <FaSyncAlt className="me-1" />
                          {capitalizeFirstLetter(schedule?.recurrence_type)}
                        </span>
                        {schedule?.recurrence_days && (
                          <small className="text-muted">
                            {capitalizeDays(schedule.recurrence_days)}
                          </small>
                        )}
                      </div>
                    </td>
                    {/* status */}
                    {!isAdmin && (
                      <td className="position-relative">
                        <span
                          className={statusInfo.className}
                          style={{
                            cursor: isUpdateLoading ? "not-allowed" : "pointer",
                            opacity: isUpdateLoading ? 0.7 : 1,
                            pointerEvents: isUpdateLoading ? "none" : "auto",
                          }}
                          // onClick={() => {
                          //   if (isUpdateLoading) return;

                          //   setOpenStatusId(
                          //     openStatusId === schedule.id ? null : schedule.id,
                          //   );
                          // }}
                        >
                          {statusInfo.label}
                        </span>

                        {openStatusId === schedule.id && (
                          <div
                            className="position-absolute bg-white border rounded shadow-sm mt-1"
                            style={{ zIndex: 1000 }}
                          >
                            {STATUS_OPTIONS.map((st) => {
                              const info = getScheduleStatusInfo(st, t);

                              return (
                                <div
                                  key={st}
                                  className="px-3 py-2"
                                  style={{
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                  }}
                                  onClick={() =>
                                    handleStatusChange(schedule, st)
                                  }
                                >
                                  <span className={info.className}>
                                    {info.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    )}

                    {/* <td>{schedule?.created_by_name || "N/A"}</td> */}
                    <td>
                      <ActionMenu
                        actions={[
                          {
                            label: t("edit"),
                            icon: <MdOutlineEdit size={16} />,
                            onClick: () => navigate(`edit/${schedule.id}`),
                            hidden: isAdmin,
                          },
                          {
                            label: `${t("view")} ${t("schedule")} ${t("details")}`,
                            icon: <IoEye size={15} />,
                            onClick: () => handleShowScheduleDetails(schedule),
                          },
                          {
                            label: t("delete"),
                            icon: <FaTrash size={15} />,
                            onClick: () => setDeleteTarget(schedule),
                            danger: true,
                            hidden: isAdmin || schedule.status === "scheduled",
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="text-center">
                <td colSpan="8">
                  <div>
                    <span>{`${t("no")} ${t("schedules")} ${t("found")}`}</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Schedule Details Modal */}
      {showScheduleDetails && scheduleDetails && (
        <Modal
          show={showScheduleDetails}
          onHide={handleCloseModal}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="fs-5 fw-bold">
              {t("schedule")} {t("details")} -{" "}
              {scheduleDetails?.driver_name || "N/A"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            className="pt-0"
            style={{
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div className="schedule-details-container">
              {/* Basic Information Card */}
              <div className="card mb-3 border-0 shadow-sm">
                <div className="card-header bg-light border-0">
                  <h6 className="mb-0">
                    <FaCalendarAlt className="me-2 text-primary" />
                    {t("basic_info")}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("driver")} {t("name")}
                      </small>
                      <strong>{scheduleDetails?.driver_name || "N/A"}</strong>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("driver")} {t("email")}
                      </small>
                      <strong>{scheduleDetails?.driver_email || "N/A"}</strong>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("start")} {t("date")}
                      </small>
                      <strong>
                        {formatDateToLocal(scheduleDetails?.start_date) ||
                          "N/A"}
                      </strong>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("end")} {t("date")}
                      </small>
                      <strong>
                        {formatDateToLocal(scheduleDetails?.end_date) || "N/A"}
                      </strong>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("shift")} {t("type")}
                      </small>
                      <span className="badge bg-info">
                        {t(scheduleDetails?.shift_type) || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Information Card */}
              <div className="card mb-3 border-0 shadow-sm">
                <div className="card-header bg-light border-0">
                  <h6 className="mb-0">
                    <FaClock className="me-2 text-primary" />
                    {t("time")} {t("info")}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {scheduleDetails?.shift_type === "afternoon"
                          ? `${t("afternoon")} ${t("time")}`
                          : `${t("morning")} ${t("time")}`}
                      </small>
                      <strong>
                        {convertTime(scheduleDetails?.start_time) || "N/A"} –{" "}
                        {convertTime(scheduleDetails?.end_time) || "N/A"}
                      </strong>
                    </div>
                    {scheduleDetails?.shift_type === "morning_afternoon" &&
                      scheduleDetails?.return_start_time && (
                        <div className="col-6 mb-2">
                          <small className="text-muted d-block">
                            {t("afternoon")} {t("time")}
                          </small>
                          <strong>
                            {convertTime(scheduleDetails.return_start_time)} –{" "}
                            {convertTime(scheduleDetails.return_end_time)}
                          </strong>
                        </div>
                      )}
                    {/* <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("actual_time")}
                      </small>
                      <strong>
                        {scheduleDetails?.actual_start_time
                          ? `${convertTime(
                              scheduleDetails.actual_start_time
                            )} - ${convertTime(
                              scheduleDetails.actual_end_time
                            )}`
                          : t("not_started")}
                      </strong>
                    </div> */}
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("status")}
                      </small>
                      <span
                        className={
                          getScheduleStatusInfo(scheduleDetails?.status, t)
                            .className
                        }
                      >
                        {getScheduleStatusInfo(scheduleDetails?.status, t).label}
                      </span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">
                        {t("created_by")}
                      </small>
                      <strong>
                        {scheduleDetails?.created_by_name || "N/A"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recurrence Information Card - Only show if recurring */}
              {/* {scheduleDetails?.is_recurring && ( */}
              <div className="card mb-3 border-0 shadow-sm">
                <div className="card-header bg-light border-0">
                  <h6 className="mb-0">
                    <FaSyncAlt className="me-2 text-primary" />
                    {t("recurrence_info")}
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-12 mb-2">
                      <div className="d-flex align-items-center">
                        <span className="badge bg-success me-2">
                          {t("recurring")}
                        </span>
                        <strong>
                          {capitalizeFirstLetter(
                            scheduleDetails?.recurrence_type,
                          )}
                        </strong>
                      </div>
                    </div>

                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">{t("days")}</small>
                      <strong>
                        {capitalizeDays(scheduleDetails?.recurrence_days)}
                      </strong>
                    </div>

                    {/* {scheduleDetails?.recurrence_start_date && (
                      <div className="col-6 mb-2">
                        <small className="text-muted d-block">
                          {t("start_date")}
                        </small>
                        <strong>
                          {formatDateToLocal(
                            scheduleDetails.recurrence_start_date
                          )}
                        </strong>
                      </div>
                    )} */}

                    {/* {scheduleDetails?.recurrence_end_date && (
                      <div className="col-6 mb-2">
                        <small className="text-muted d-block">
                          {t("end_date")}
                        </small>
                        <strong>
                          {formatDateToLocal(
                            scheduleDetails.recurrence_end_date
                          )}
                        </strong>
                      </div>
                    )} */}
                  </div>
                </div>
              </div>
              {/* )} */}

              {/* Notes Card - Only show if notes exist */}
              {scheduleDetails?.notes && (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light border-0">
                    <h6 className="mb-0">{t("notes")}</h6>
                  </div>
                  <div className="card-body">
                    <p className="mb-0">{scheduleDetails.notes}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="mt-3 pt-3 border-top text-center">
                <small className="text-muted">
                  {t("created_at")}:{" "}
                  {formatDateToLocal(scheduleDetails?.created_at)} |{" "}
                  {t("updated_at")}:{" "}
                  {formatDateToLocal(scheduleDetails?.updated_at)}
                </small>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="border-0">
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              className="px-4"
            >
              {t("btns.close")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fs-5 fw-bold">
            {t("delete")} {t("schedule")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-1">{t("delete_schedule_confirm")}</p>
          {deleteTarget && (
            <div className="mt-2 p-3 rounded bg-light small">
              <div>
                <strong>
                  {t("shift")} {t("type")}:
                </strong>{" "}
                {deleteTarget.shift_type}
              </div>
              <div>
                <strong>{t("date")}:</strong>{" "}
                {formatDateToLocal(deleteTarget.start_date)} —{" "}
                {formatDateToLocal(deleteTarget.end_date)}
              </div>
              <div className="mt-1">
                <span
                  className={
                    getScheduleStatusInfo(deleteTarget.status, t).className
                  }
                >
                  {getScheduleStatusInfo(deleteTarget.status, t).label}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            {t("btns.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isDeleteLoading}
          >
            {isDeleteLoading ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              t("btns.delete")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .status {
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
          width: 100px;
          text-align: center;
        }
        .status.scheduled {
          background-color: #e7f1ff;
          color: #004085;
          border: 1px solid #b8daff;
        }
        .status.available {
          background-color: #d1f2eb;
          color: #0c5460;
          border: 1px solid #bee5eb;
        }
        .status.completed {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .status.cancelled {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        .status.unknown {
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        }

        .actionBtn .iconBtn {
          padding: 6px;
          width: 36px;
          height: 36px;
        }
        .schedule-details-container .card {
          border: 1px solid #dee2e6;
          border-radius: 8px;
        }
        .schedule-details-container .card-body {
          padding: 16px;
        }
        .table-sm th {
          font-weight: 600;
          color: #495057;
          background-color: #f8f9fa;
        }
        .table-sm td,
        .table-sm th {
          padding: 8px;
          vertical-align: middle;
        }

        .badge {
          padding: 5px 10px;
          font-size: 12px;
          font-weight: 500;
          border-radius: 6px;
        }

        .badge.bg-info {
          background-color: #17a2b8 !important;
        }

        .badge.bg-success {
          background-color: #28a745 !important;
        }

        .card {
          border-radius: 10px;
          border: 1px solid #e0e0e0;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e0e0e0;
          padding: 12px 20px;
          border-radius: 10px 10px 0 0 !important;
        }

        .card-body {
          padding: 20px;
        }

        .text-muted {
          font-size: 12px;
          color: #6c757d !important;
        }

        strong {
          font-weight: 600;
          color: #343a40;
        }

        .shadow-sm {
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
        }

        .border-top {
          border-top: 1px solid #e0e0e0 !important;
        }

        .modal-header .close {
          padding: 10px;
          margin: -10px -10px -10px auto;
        }

        .modal-title {
          color: #343a40;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          padding: 15px 20px;
        }
        .spinner-border {
          width: 1rem;
          height: 1rem;
          border-width: 2px;
        }
      `}</style>
    </>
  );
};

export default Schedules;
